import React from 'react';
import type { ScenarioLocation } from '../../lib/planningData';
import type { MultiKey } from '../core/MultiKey';
import type { ClusterId, LocationId } from '@seaber/common/core/entries';

export type LocationPair = readonly [
  fromLocationId: LocationId,
  toLocationId: LocationId,
  avoidsChannels: boolean,
  draft: number | null,
  noGoZones: string,
];

export type OverrideParams = [
  fromLocationId: LocationId,
  toLocationId: LocationId,
  avoidsChannels: boolean,
];

export type RouteParams = [
  fromClusterId: ClusterId,
  toClusterId: ClusterId,
  avoidsChannels: boolean,
  draft: number | null,
  noGoZones: string,
];

export type RouteData = {
  is_override: boolean;
  dist: number;
  inside_nm?: number;
  outside_nm?: number;
  used_channels?: string[];
  /**
   * Set when no route could be obtained for these parameters. The entry still
   * carries `dist: 0` so callers that only need the matrix populated keep
   * working, but a zero here is an unknown rather than a measurement.
   */
  unresolved?: boolean;
};

export type LocationPairNullish = [
  fromLocationId: LocationId,
  toLocationId: LocationId,
  avoidsChannels: boolean,
  draft?: number | null,
  noGoZones?: string,
];

export type Distances = {
  get: (...pair: LocationPairNullish) => RouteData | undefined;
  set: (data: RouteData, ...pair: RouteParams) => void;
  map: MultiKey<RouteData, RouteParams>;
  overrides: MultiKey<RouteData, OverrideParams>;
};

export interface DistanceContextProps {
  distances: Distances;
  noGoZones: string;
  queueDistancesForLocationPairs: (locationPairs: LocationPair[]) => void;
  queueAllDistancesBetweenLocations: (
    locationIds: Set<LocationId | null | undefined>,
  ) => void;
  fetchMissingDistances: (
    existingDistances: Distances,
    locationIds: Set<LocationId>,
    includeAvoidsChannels?: boolean,
  ) => Promise<Distances>;
  resetDistances: () => void;
  setLocations: (locations: Map<LocationId, ScenarioLocation>) => void;
}

export const DistanceContext = React.createContext<
  DistanceContextProps | undefined
>(undefined);
